import { css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { TeComponent } from './common/te-component';
import './te-text';
import { theme } from './common/theme';
import { styleMap } from 'lit/directives/style-map';

type AlphaLevel = '' | '00';

/**
 * Tooltip Component
 *
 * @slot - Default slot for tooltip wrappers children
 * @slot tooltip-content - Slot for contents of the tooltip
 */
@customElement('te-tooltip')
export class TeTooltip extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-tooltip {
        display: flex;
        position: relative;
      }

      .gap-filler {
        width: 0;
        height: 0;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 100;
      }

      .gradiant-bottom {
        bottom: 0;
      }

      .gradiant-bottom,
      .gradiant-top {
        position: absolute;
        left: 0;
        height: 40px;
        opacity: 1;
        width: 100%;
        transform: matrix(-1, 0, 0, -1, 0, 0);
        visibility: hidden;
      }

      .gradiant-top {
        top: 0;
      }

      .tooltip-text {
        display: none;
        visibility: hidden;
        border-radius: 4px;
        box-shadow: ${theme.shadows.primary};
        position: absolute;
        z-index: 100;
        transition: 200ms ease-in-out;
        padding: 12px 16px;
        overflow: auto;
        opacity: 0;
        pointer-events: none;
      }

      .tooltip-text.top {
        transform: translateX(-50%) translateY(calc(-100% - 16px));
      }

      .tooltip-text.bottom {
        transform: translateX(-50%) translateY(24px);
      }

      .tooltip-text.left {
        transform: translateX(calc(-100% - 16px)) translateY(-50%);
      }

      .tooltip-text.right {
        transform: translateX(24px) translateY(-50%);
      }

      .te-tooltip:hover .tooltip-text {
        display: block;
        visibility: visible;
        opacity: 1;
        pointer-events: auto;
      }
    `,
  ];

  /** Relative positioning of the tooltip */
  @property({ type: String }) placement: 'top' | 'bottom' | 'right' | 'left' = 'top';
  /** Pre-themed tooltip theme */
  @property({ type: String }) variant: 'default' | 'highlight' | 'secondary' = 'default';
  /** Maximum height for tooltip */
  @property({ type: String }) maxHeight = '114px';
  /** Maximum width for tooltip */
  @property({ type: String }) maxWidth = '256px';
  /** Text content for tooltip */
  @property({ type: String }) text = '';

  @query('.te-tooltip') _tooltipContainer?: HTMLElement;
  @query('#gap-filler') _gapFiller?: HTMLElement;
  @query('.tooltip-text') _tooltipText?: HTMLElement;
  @query('.gradiant-bottom') _gradiantBottom?: HTMLElement;
  @query('.gradiant-top') _gradiantTop?: HTMLElement;
  @query('#tooltip-content') _tooltipContentSlot?: HTMLSlotElement;

  @state() tooltipContent = false;

  private _hideTooltipTimeout?: NodeJS.Timeout;

  getBackgroundStyle() {
    return {
      default: theme.colors.shade0,
      secondary: theme.colors.shade5,
      highlight: theme.colors.primary.v0,
    }[this.variant];
  }

  handleTooltipTextScroll() {
    const { _tooltipText, _gradiantTop, _gradiantBottom } = this;

    if (!_tooltipText || !_gradiantBottom || !_gradiantTop) return;

    const hasScrolledToTop = _tooltipText.scrollTop === 0;
    const hasScrolledToBottom =
      _tooltipText.scrollHeight - Math.abs(_tooltipText.scrollTop) === _tooltipText.clientHeight;

    _gradiantTop.style.top = `${_tooltipText.scrollTop}px`;
    _gradiantBottom.style.bottom = `-${_tooltipText.scrollTop}px`;

    _gradiantTop.style.visibility = hasScrolledToTop ? 'hidden' : 'visible';
    _gradiantBottom.style.visibility = hasScrolledToBottom ? 'hidden' : 'visible';
  }

  getTopGradiantBackgroundColor() {
    return this.getGradiantColor('00', '');
  }

  getBottomGradiantBackgroundColor() {
    return this.getGradiantColor('', '00');
  }

  getGradiantColor(topAlphaLevel: AlphaLevel, bottomAlphaLevel: AlphaLevel) {
    const currentBackgroundColor = this.getBackgroundStyle();

    return {
      background: `transparent
        linear-gradient(
          180deg,
          ${currentBackgroundColor}${topAlphaLevel} 0%,
          ${currentBackgroundColor}AC 46%,
          ${currentBackgroundColor}${bottomAlphaLevel} 100%
        )
        0% 0% no-repeat padding-box;
      `,
    };
  }

  private onDocumentScrolled = () => {
    const { _tooltipText, _gradiantBottom, _gradiantTop } = this;
    if (!_tooltipText || !_gradiantBottom || !_gradiantTop) return;

    _tooltipText.style.transition = 'none';
    _tooltipText.style.visibility = 'hidden';
    _gradiantBottom.style.visibility = 'hidden';
    _gradiantTop.style.visibility = 'hidden';
  };

  onMouseEnterTooltip() {
    document.addEventListener('scroll', this.onDocumentScrolled);

    const { _tooltipText, _gradiantBottom, _tooltipContainer } = this;

    if (!_tooltipText || !_gradiantBottom || !_tooltipContainer) return;

    if (this._hideTooltipTimeout) {
      clearTimeout(this._hideTooltipTimeout);
      this._hideTooltipTimeout = undefined;
    }

    _tooltipText.style.position = 'fixed';
    _tooltipText.style.display = 'block';

    const hasScrolledToBottom =
      _tooltipText.scrollHeight - Math.abs(_tooltipText.scrollTop) === _tooltipText.clientHeight;
    if (_tooltipText.scrollHeight > _tooltipText.clientHeight && !hasScrolledToBottom) {
      _gradiantBottom.style.visibility = 'visible';
    }

    const textRect = _tooltipText.getBoundingClientRect();
    const tooltipRect = _tooltipContainer.getBoundingClientRect();
    this.handleTooltipShown(tooltipRect, textRect);
  }

  onMouseLeaveTooltip() {
    document.removeEventListener('scroll', this.onDocumentScrolled);

    const { _tooltipText, _gapFiller, _tooltipContainer } = this;

    if (!_tooltipText || !_gapFiller || !_tooltipContainer) return;

    _tooltipText.style.transition = '';
    _tooltipText.style.display = 'block';
    _tooltipText.style.visibility = '';
    _gapFiller.style.width = '0';
    _gapFiller.style.height = '0';

    this._hideTooltipTimeout = setTimeout(() => {
      if (!_tooltipText) return;

      _tooltipText.style.top = '';
      _tooltipText.style.left = '';
      _tooltipText.style.transform = '';
    }, 200);

    const tooltipRect = _tooltipContainer.getBoundingClientRect();
    this.handleTooltipHidden(tooltipRect);
  }

  handleTooltipShown(tooltipRect: DOMRect, textRect: DOMRect) {
    const { _tooltipText, _gapFiller } = this;

    if (!_tooltipText || !_gapFiller) return;

    const { top, right, bottom, left, height, width } = tooltipRect;

    switch (this.placement) {
      case 'top':
        _tooltipText.style.top = `${top - 8}px`;
        _tooltipText.style.left = `${(left + right) / 2}px`;
        _tooltipText.style.transform = 'translateX(-50%) translateY(-100%)';

        _gapFiller.style.width = `${textRect.width}px`;
        _gapFiller.style.height = `${24 + height / 2}px`;
        _gapFiller.style.top = '-24px';
        _gapFiller.style.transform = `translateX(calc(-50% + ${width / 2}px))`;
        break;
      case 'right':
        _tooltipText.style.top = `${top + height / 2}px`;
        _tooltipText.style.left = `${right}px`;
        _tooltipText.style.transform = 'translateX(8px) translateY(-50%)';

        _gapFiller.style.width = `${24 + width / 2}px`;
        _gapFiller.style.height = `${textRect.height}px`;
        _gapFiller.style.left = '50%';
        _gapFiller.style.transform = `translateY(calc(-50% + ${height / 2}px))`;
        break;
      case 'bottom':
        _tooltipText.style.top = `${bottom}px`;
        _tooltipText.style.left = `${(left + right) / 2}px`;
        _tooltipText.style.transform = 'translateX(-50%) translateY(8px)';

        _gapFiller.style.width = `${textRect.width}px`;
        _gapFiller.style.height = `${24 + height / 2}px`;
        _gapFiller.style.top = '50%';
        _gapFiller.style.transform = `translateX(calc(-50% + ${width / 2}px))`;
        break;
      case 'left':
        _tooltipText.style.top = `${top + height / 2}px`;
        _tooltipText.style.left = `${left - 8}px`;
        _tooltipText.style.transform = 'translateX(-100%) translateY(-50%)';

        _gapFiller.style.width = `${24 + width / 2}px`;
        _gapFiller.style.height = `${textRect.height}px`;
        _gapFiller.style.left = '-24px';
        _gapFiller.style.transform = `translateY(calc(-50% + ${height / 2}px))`;
        break;
    }
  }

  handleTooltipHidden(tooltipRect: DOMRect) {
    if (!this._tooltipText) return;

    const { top, right, bottom, left } = tooltipRect;

    switch (this.placement) {
      case 'top':
        this._tooltipText.style.top = `${top - 16}px`;
        break;
      case 'right':
        this._tooltipText.style.left = `${right + 8}px`;
        break;
      case 'bottom':
        this._tooltipText.style.top = `${bottom + 8}px`;
        break;
      case 'left':
        this._tooltipText.style.left = `${left - 16}px`;
        break;
    }
  }

  updated() {
    if (this._tooltipContentSlot) {
      this.tooltipContent = this._tooltipContentSlot.assignedNodes({ flatten: true }).length > 0;
    }
  }

  getTooltipContent() {
    if (this.tooltipContent) {
      return;
    }

    const colorText =
      this.variant === 'default' ? theme.colors.text.toString() : theme.colors.shade0.toString();

    return html`
      <te-text color=${colorText}>
        ${this.text}
      </te-text>
    `;
  }

  render() {
    return html`
      <style>
        .tooltip-text {
          background: ${this.getBackgroundStyle().toString()};
          max-width: ${this.maxWidth};
          max-height: ${this.maxHeight};
        }
      </style>

      <div
        class="te-tooltip"
        @mouseenter=${this.onMouseEnterTooltip}
        @mouseleave=${this.onMouseLeaveTooltip}
      >
        <slot class="tooltip-slot" id="tooltip-slot"></slot>
        <div class="gap-filler" id="gap-filler"></div>

        <div class="tooltip-text ${this.placement}" @scroll=${this.handleTooltipTextScroll}>
          <div class="gradiant-top" style=${styleMap(this.getTopGradiantBackgroundColor())}></div>
          <div
            class="gradiant-bottom"
            style=${styleMap(this.getBottomGradiantBackgroundColor())}
          ></div>
          <div>
            <slot name="tooltip-content" id="tooltip-content"></slot>
            ${this.getTooltipContent()}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-tooltip': TeTooltip;
  }
}
